// client/src/pages/AdminOrderList.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Select, MenuItem, Chip, Alert,
  CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const statusColor = { Pending: 'warning', Shipped: 'info', Completed: 'success', Cancelled: 'default' };
const paymentColor = { unpaid: 'warning', paid: 'success', failed: 'error' };
const STATUS_OPTIONS = ['Pending', 'Shipped', 'Completed', 'Cancelled'];

const AdminOrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trackingTarget, setTrackingTarget] = useState(null);
  const [trackingForm, setTrackingForm] = useState({ carrierName: '', trackingNumber: '' });
  const { token } = useContext(AuthContext);

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch orders. You may not have permission.');
      setOrders(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleStatusChange = async (orderId, status) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error('Failed to update order status.');
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSaveTracking = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${trackingTarget.id}/shipping`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(trackingForm),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save tracking info.');
      setTrackingTarget(null);
      fetchOrders();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Order Management</Typography>
      {orders.length === 0 ? (
        <EmptyState icon={<ReceiptLongOutlinedIcon />} title="No orders yet" description="Customer orders will appear here." />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell><TableCell>Customer</TableCell><TableCell>Items</TableCell>
                <TableCell>Date</TableCell><TableCell>Fulfillment</TableCell><TableCell align="right">Total</TableCell>
                <TableCell>Payment</TableCell><TableCell>Status</TableCell><TableCell>Tracking</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell><Link to={`/orders/${order.id}`}>#{order.id}</Link></TableCell>
                  <TableCell>{order.customer_email}</TableCell>
                  <TableCell>{order.product_names}</TableCell>
                  <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                  <TableCell><Chip label={order.fulfillment_type === 'collection' ? 'Collection' : 'Delivery'} size="small" variant="outlined" /></TableCell>
                  <TableCell align="right">{formatCurrency(order.total_amount)}</TableCell>
                  <TableCell>
                    <Chip label={order.payment_status} size="small" color={paymentColor[order.payment_status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small" value={order.status}
                      onChange={(e) => handleStatusChange(order.id, e.target.value)}
                      renderValue={(val) => <Chip label={val} size="small" color={statusColor[val] || 'default'} />}
                    >
                      {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell>
                    {order.tracking_number ? (
                      <Box>
                        <Typography variant="body2">{order.carrier_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{order.tracking_number}</Typography>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        onClick={() => { setTrackingTarget(order); setTrackingForm({ carrierName: '', trackingNumber: '' }); }}
                      >
                        Add Tracking
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(trackingTarget)} onClose={() => setTrackingTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Add Tracking for Order #{trackingTarget?.id}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Carrier / Courier Company" fullWidth
              placeholder="e.g. The Courier Guy, PostNet, Aramex"
              value={trackingForm.carrierName}
              onChange={(e) => setTrackingForm({ ...trackingForm, carrierName: e.target.value })}
            />
            <TextField
              label="Tracking Number" fullWidth
              value={trackingForm.trackingNumber}
              onChange={(e) => setTrackingForm({ ...trackingForm, trackingNumber: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!trackingForm.carrierName || !trackingForm.trackingNumber}
            onClick={handleSaveTracking}
          >
            Save & Mark Shipped
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminOrderList;
